import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction, useState, useEffect } from "react"
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import { Dropdown, Table } from "@/components"
import { CarrierRatesType, CarrierType, MexicoMarketType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import { VehicleCard } from "../user/VehicleCard"
import Swal from "sweetalert2"
import { convertEquipmentType, dollarUS } from "@/lib/services"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { data: CarrierRatesType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: CarrierRatesType) => void
  handleFormUpdate: (form: CarrierRatesType) => void
  handleFormDelete: (form: string) => void
  carrierListing: { data: CarrierType[] }
  listOfMexicoMarket: { data: MexicoMarketType[] }
  transloadCarrierList: { id: string; transloadCarrier: string }[]
  title: string
  setTitle: (title: string) => void
}

export const CarrierRates: FC<Props> = (props) => {
  const [carrier, setCarrier] = useState<Dropdown>({
    id: "",
    name: "",
  })

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CarrierRatesType>()

  const onSubmit = (data: CarrierRatesType) => {
    data.carrierId = carrier.id
    data.carrierName = carrier.name
    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      // color: "#ffffff",
      // background: "#333333",
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  const populateData = (e: CarrierRatesType) => {
    props.setTitle('Update Carrier Rates')
    setValue("id", e.id)
    setValue("equipment", e.equipment)
    setValue("mexicoMarketId", e.mexicoMarketId)
    setCarrier({ id: e.carrierId, name: e.carrierName })
    setValue("type", e.type)
    setValue("freightCost", e.freightCost)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      equipment: "",
      mexicoMarketId: "",
      type: "",
      freightCost: undefined,
      id: undefined,
    })
    setCarrier({ id: "", name: "" })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="medium"
        title={props.title}
      >
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card"
                value="DRY_VAN"
                {...register("equipment", { required: true })}
              />
              <label htmlFor="vehicle_card">
                <VehicleCard image="/images/dry-van.svg" name="Dry Van" />
              </label>
            </div>
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card1"
                value="FLATBED"
                {...register("equipment", { required: true })}
              />
              <label htmlFor="vehicle_card1">
                <VehicleCard image="/images/flatbed.svg" name="Flatbed" />
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs text-accent">Mexico Market</label>
              <select
                className={
                  (errors.equipment ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-primary-1 bg-white mt-1 outline-none dark:text-primary-2 text-light-2 border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                }
                {...register("mexicoMarketId", { required: true })}
              >
                <option value="" hidden>
                  Select Mexico Market
                </option>
                {props.listOfMexicoMarket.data.map((e, i) => (
                  <option key={i} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Carrier</label>
              <div className="mt-1">
                <Dropdown
                  listingData={props.carrierListing.data}
                  selected={carrier}
                  setSelected={setCarrier}
                />
              </div>
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Type</label>
              <select
                className={
                  (errors.type ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-primary-1 bg-white mt-1 outline-none dark:text-primary-10 border-2 rounded dark:placeholder:text-zinc-500 text-light-2 placeholder:text-xs"
                }
                placeholder="Enter Rate Per Mile"
                {...register("type", { required: true })}
              >
                <option value="" hidden>
                  Select Type
                </option>
                <option value="Southbound">Southbound</option>
                <option value="Northbound">Northbound</option>
              </select>
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-2">MX Freight Cost</label>
              <input
                className={
                  (errors.freightCost ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white mt-1 outline-none dark:text-white text-light-2 border-2 rounded dark:placeholder:text-zinc-500 placeholder:text-light-2 placeholder:text-xs"
                }
                type="number"
                placeholder="Enter MX Freight Cost"
                {...register("freightCost", { required: true })}
              />
            </fieldset>
          </div>
        </div>
        <button
          className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
          onClick={handleSubmit(onSubmit)}
          type="button"
        >
          Submit
        </button>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.data.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-64 truncate"}>
                {e.carrierName}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.type == null
                  ? "-"
                  : e.type == "Southbound"
                  ? "Southbound"
                  : e.type == "Northbound"
                  ? "Northbound"
                  : null}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {convertEquipmentType(e.equipment) == "Van"
                  ? "Dry Van"
                  : convertEquipmentType(e.equipment)}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.mexicoMarket.name}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {dollarUS.format(e.freightCost)}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.transloadPartner == null
                  ? "-"
                  : dollarUS.format(
                    e.freightCost + e.transloadPartner.transloadCost,
                  )}
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                  <TrashIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
